import { supabase } from '../lib/supabase';

export const taxAdjustmentsService = {
  // Create tax adjustment
  async createTaxAdjustment(adjustmentData) {
    try {
      const { data, error } = await supabase?.from('tax_adjustments')?.insert([adjustmentData])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Create tax adjustment error:', error);
      return { data: null, error: error?.message || 'Failed to create tax adjustment' };
    }
  },

  // Get tax adjustments for organization
  async getTaxAdjustments(organizationId, options = {}) {
    try {
      const { page = 1, limit = 20, adjustmentType, dateFrom, dateTo, search } = options;
      const offset = (page - 1) * limit;

      let query = supabase?.from('tax_adjustments')?.select(`
          *,
          created_by:created_by(full_name, email),
          approved_by:approved_by(full_name, email),
          journal_entries:journal_entry_id(entry_number, status)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      if (adjustmentType) query = query?.eq('adjustment_type', adjustmentType);
      if (dateFrom) query = query?.gte('adjustment_date', dateFrom);
      if (dateTo) query = query?.lte('adjustment_date', dateTo);
      if (search) {
        query = query?.or(`reference_number.ilike.%${search}%,description.ilike.%${search}%,tax_code.ilike.%${search}%`);
      }

      query = query?.range(offset, offset + limit - 1);

      const { data, error } = await query;
      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Get tax adjustments error:', error);
      return { data: [], error: error?.message || 'Failed to fetch tax adjustments' };
    }
  },

  // Update tax adjustment
  async updateTaxAdjustment(adjustmentId, updates) {
    try {
      const { data, error } = await supabase?.from('tax_adjustments')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', adjustmentId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update tax adjustment error:', error);
      return { data: null, error: error?.message || 'Failed to update tax adjustment' };
    }
  },

  // Approve tax adjustment
  async approveTaxAdjustment(adjustmentId, userId) {
    try {
      const { data, error } = await supabase?.from('tax_adjustments')?.update({
          approved_by: userId,
          approved_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.eq('id', adjustmentId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Approve tax adjustment error:', error);
      return { data: null, error: error?.message || 'Failed to approve tax adjustment' };
    }
  },

  // Link journal entry to tax adjustment
  async linkJournalEntry(adjustmentId, journalEntryId) {
    try {
      const { data, error } = await supabase?.from('tax_adjustments')?.update({
          journal_entry_id: journalEntryId,
          updated_at: new Date()?.toISOString()
        })?.eq('id', adjustmentId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Link journal entry error:', error);
      return { data: null, error: error?.message || 'Failed to link journal entry' };
    }
  },

  // Calculate VAT adjustment
  async calculateVATAdjustment(originalAmount, oldRate, newRate) {
    try {
      const oldVat = originalAmount * (oldRate / 100);
      const newVat = originalAmount * (newRate / 100);
      const adjustment = newVat - oldVat;
      const adjustedAmount = originalAmount + adjustment;

      return {
        originalAmount: parseFloat(originalAmount),
        oldRate: parseFloat(oldRate),
        newRate: parseFloat(newRate),
        oldVat: parseFloat(oldVat?.toFixed(2)),
        newVat: parseFloat(newVat?.toFixed(2)),
        adjustmentAmount: parseFloat(adjustment?.toFixed(2)),
        adjustedAmount: parseFloat(adjustedAmount?.toFixed(2)),
        error: null
      };
    } catch (error) {
      console.error('Calculate VAT adjustment error:', error);
      return { error: error?.message || 'Failed to calculate VAT adjustment' };
    }
  },

  // Get tax adjustment by ID
  async getTaxAdjustmentById(adjustmentId) {
    try {
      const { data, error } = await supabase?.from('tax_adjustments')?.select(`
          *,
          created_by:created_by(full_name, email),
          approved_by:approved_by(full_name, email),
          journal_entries:journal_entry_id(id, entry_number, status, description)
        `)?.eq('id', adjustmentId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get tax adjustment error:', error);
      return { data: null, error: error?.message || 'Failed to fetch tax adjustment' };
    }
  },

  // Get tax adjustments summary by period
  async getTaxAdjustmentsSummary(organizationId, startDate, endDate) {
    try {
      const { data, error } = await supabase?.from('tax_adjustments')?.select('adjustment_type, adjustment_amount, tax_rate')?.eq('organization_id', organizationId)?.gte('adjustment_date', startDate)?.lte('adjustment_date', endDate);

      if (error) throw error;

      // Calculate summary statistics
      const summary = {
        totalAdjustments: data?.length || 0,
        totalAmount: data?.reduce((sum, adj) => sum + parseFloat(adj?.adjustment_amount || 0), 0) || 0,
        byType: {},
        averageRate: 0
      };

      // Group by adjustment type
      data?.forEach(adjustment => {
        const type = adjustment?.adjustment_type;
        if (!summary?.byType?.[type]) {
          summary.byType[type] = {
            count: 0,
            totalAmount: 0,
            rates: []
          };
        }
        summary.byType[type].count++;
        summary.byType[type].totalAmount += parseFloat(adjustment?.adjustment_amount || 0);
        if (adjustment?.tax_rate) {
          summary?.byType?.[type]?.rates?.push(parseFloat(adjustment?.tax_rate));
        }
      });

      // Calculate average rates per type
      Object.keys(summary?.byType)?.forEach(type => {
        const rates = summary?.byType?.[type]?.rates;
        if (rates?.length > 0) {
          summary.byType[type].averageRate = rates?.reduce((sum, rate) => sum + rate, 0) / rates?.length;
        }
      });

      return { data: summary, error: null };
    } catch (error) {
      console.error('Get tax adjustments summary error:', error);
      return { data: null, error: error?.message || 'Failed to fetch tax adjustments summary' };
    }
  },

  // Delete tax adjustment
  async deleteTaxAdjustment(adjustmentId) {
    try {
      const { error } = await supabase?.from('tax_adjustments')?.delete()?.eq('id', adjustmentId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Delete tax adjustment error:', error);
      return { error: error?.message || 'Failed to delete tax adjustment' };
    }
  }
};